/**
 * Platform RBAC — capability sets for the VagusX-side directory.
 *
 * Phase 1 defines the capability sets as code constants rather than in the
 * database. Only one role (`platform_admin`) exists today and it carries the
 * full `ALL_PERMISSIONS` set at scope `'all'`. Intersection with any org
 * user's permissions is then effectively a pass-through — the platform admin
 * can do whatever the impersonated user can do, and nothing more.
 *
 * When tiered platform roles are introduced (e.g. `platform_support`,
 * `platform_auditor`), this constant becomes a DB table with the same shape.
 * Intersection logic does not need to change.
 */
import { P, ALL_PERMISSIONS, type PermissionString } from './rbac';

export const PLATFORM_ROLE = {
  PLATFORM_ADMIN: 'platform_admin',
} as const;

export type PlatformRole = (typeof PLATFORM_ROLE)[keyof typeof PLATFORM_ROLE];

/** A scope for a permission. `null` means "no scoping applies". */
export type Scope = 'all' | 'own' | null;

export interface PermissionWithScope {
  permission: PermissionString;
  scope: Scope;
}

/**
 * Scope ordering for `minScope()`. Higher = broader authority.
 *   null (no scope) < 'own' (only caller's own resources) < 'all' (everything in tenant)
 *
 * Expressed as a constant so the ordering is visible at the call site and
 * the function body remains trivial.
 */
const SCOPE_RANK: Record<'null' | 'own' | 'all', number> = {
  null: 0,
  own: 1,
  all: 2,
} as const;

/**
 * Returns the narrower (lower-rank) of two scopes.
 * Used when intersecting a platform capability with an org user permission —
 * the effective scope is the tighter of the two.
 */
export function minScope(a: Scope, b: Scope): Scope {
  const ra = SCOPE_RANK[a ?? 'null'];
  const rb = SCOPE_RANK[b ?? 'null'];
  return ra <= rb ? a : b;
}

/**
 * Capability set for each platform role. For phase 1, only `platform_admin`
 * is defined — it holds the union of every permission at scope `'all'`. When
 * we split into tiered roles, add new rows here (or migrate this constant
 * into a DB table keyed by role name with one row per permission/scope).
 */
export const PLATFORM_ROLE_CAPABILITIES: Record<PlatformRole, PermissionWithScope[]> = {
  [PLATFORM_ROLE.PLATFORM_ADMIN]: ALL_PERMISSIONS.map((p) => ({
    permission: p,
    scope: 'all' as const,
  })),
};

/**
 * Compute effective permissions during an impersonation session.
 *
 *   effective = { p | p ∈ platformCaps AND p ∈ orgPerms }
 *   for each such p, scope(p) = min(scope_platform(p), scope_org(p))
 *
 * A permission is in the effective set only if BOTH the platform role and
 * the impersonated org user have it. The effective scope is the narrower of
 * the two (a platform `all` × org `own` yields `own`).
 *
 * Pure function. Callers must deduplicate inputs if they care about
 * idempotence — this function does not.
 */
export function effectivePermissions(
  platformCaps: readonly PermissionWithScope[],
  orgPerms: readonly PermissionWithScope[]
): PermissionWithScope[] {
  const byName = new Map<PermissionString, Scope>();
  for (const cap of platformCaps) {
    byName.set(cap.permission, cap.scope);
  }
  const out: PermissionWithScope[] = [];
  for (const op of orgPerms) {
    const platformScope = byName.get(op.permission);
    if (platformScope === undefined) continue;
    out.push({
      permission: op.permission,
      scope: minScope(platformScope, op.scope),
    });
  }
  return out;
}

/**
 * Platform-level permission strings gate `/api/platform/*` routes. They are
 * NOT in the org-level `P` constant — those govern tenant routes. Kept
 * separate so the two systems can evolve independently.
 */
export const PLATFORM_P = {
  ORGS_VIEW: 'platform:orgs:view',
  IMPERSONATE: 'platform:impersonate',
  ADMIN_USERS_VIEW: 'platform:admin_users:view',
  ADMIN_USERS_EDIT: 'platform:admin_users:edit',
} as const;

export type PlatformPermissionString = (typeof PLATFORM_P)[keyof typeof PLATFORM_P];

/**
 * Platform-role → allowed platform-permission map. Phase 1: admin gets all.
 */
export const PLATFORM_ROLE_PERMISSIONS: Record<PlatformRole, readonly PlatformPermissionString[]> =
  {
    [PLATFORM_ROLE.PLATFORM_ADMIN]: [
      PLATFORM_P.ORGS_VIEW,
      PLATFORM_P.IMPERSONATE,
      PLATFORM_P.ADMIN_USERS_VIEW,
      PLATFORM_P.ADMIN_USERS_EDIT,
    ],
  };

/** Re-export P (for callers who also need org-level permissions). */
export { P, ALL_PERMISSIONS, type PermissionString };
